// Single source of truth for /tools/what-is-my-home-worth.

export const PAGE_PATH = "/tools/what-is-my-home-worth";
export const PAGE_URL = "https://www.homeapproach.com/tools/what-is-my-home-worth";
export const PAGE_TITLE = "What Is My Home Worth Right Now?";
export const PAGE_DESCRIPTION =
  "Three ways to find out what your home is actually worth: an instant online estimate, a free agent CMA, and a licensed appraisal. Compare speed, accuracy, and cost, then get a value range for your address.";

export const SECTIONS = [
  { id: "compare", title: "Estimate vs. CMA vs. appraisal" },
  { id: "how-comps-work", title: "How real comps work" },
  { id: "estimator", title: "Get an instant estimate" },
  { id: "cma", title: "Get a local agent valuation" },
  { id: "appraisal", title: "When you need a licensed appraisal" },
  { id: "sold-homes", title: "Sold homes, not list prices" },
  { id: "market-2026", title: "2026 market factors" },
  { id: "faq", title: "Common questions" },
];

export const INTRO_POINTS = [
  {
    label: "Automated estimates guess from records",
    text: "An AVM reads tax records, past sales, and public listing data. It has never seen your kitchen. Typical national error ranges run roughly 2–8% on active listings and wider on off-market homes, which is $10,000–$40,000 on a $500,000 house.",
  },
  {
    label: "Upgrades and defects are invisible",
    text: "A gut renovation, a new roof, or a finished basement often does not appear in public data. Neither does a failing HVAC, an unpermitted addition, or a busy road at the end of the driveway. Those are exactly the things that move a real offer.",
  },
  {
    label: "Thin comp data widens the range",
    text: "Rural areas, custom homes, and neighborhoods with few recent sales give the model almost nothing to anchor on. The estimate still prints a confident-looking number — the confidence note is what actually matters.",
  },
];

export type CompareRow = {
  id: string;
  method: string;
  speed: string;
  accuracy: string;
  cost: string;
  bestFor: string;
};

export const COMPARISON: CompareRow[] = [
  {
    id: "avm",
    method: "Online estimate (AVM)",
    speed: "Seconds",
    accuracy: "Roughly ±5–10% — wider on unique or off-market homes",
    cost: "Free",
    bestFor: "A starting range when you're curious, tracking equity, or deciding whether to look further.",
  },
  {
    id: "cma",
    method: "Agent CMA (comparative market analysis)",
    speed: "1–3 days",
    accuracy: "Roughly ±3–5% — a licensed agent adjusts for condition and location",
    cost: "Free from most local agents",
    bestFor: "Pricing a listing, planning a sale, or sanity-checking an online estimate before you act on it.",
  },
  {
    id: "appraisal",
    method: "Licensed appraisal",
    speed: "1–2 weeks",
    accuracy: "The defensible number lenders and courts accept",
    cost: "Typically $400–$800",
    bestFor: "Refinancing, removing PMI, estate settlement, divorce, tax appeals, and any dispute over value.",
  },
];

export const COMPS_METHOD = {
  heading: "How a real valuation picks comps",
  bullets: [
    {
      label: "5 to 8 recent sales",
      text: "A credible CMA leans on five to eight closed sales, not one lucky neighbor. Fewer than three good comps is a signal the range should widen, not narrow.",
    },
    {
      label: "0.5 to 1 mile radius",
      text: "Half a mile in dense suburbs, up to a mile where lots are larger. Crossing a school-district or municipal boundary matters more than raw distance.",
    },
    {
      label: "3 to 6 months recency",
      text: "Sales older than six months get discounted or time-adjusted, because the rate environment underneath them was different.",
    },
    {
      label: "Same style and size band",
      text: "A comp should be within roughly 20% of your square footage, the same story count, and a similar bed/bath configuration.",
    },
  ],
};

export const ADJUSTMENT_FACTORS = [
  { factor: "Roof age", effect: "A roof past 20 years reads as a near-term cost to the buyer and typically pulls value down." },
  { factor: "Kitchen and bath condition", effect: "The two rooms that most influence perceived value, and the two AVMs are blindest to." },
  { factor: "Lot size and usability", effect: "Flat, fenced, and private beats large-but-sloped almost every time." },
  { factor: "Home office / finished basement", effect: "Adds value when permitted and heated, far less when neither is true." },
  { factor: "Garage and parking", effect: "In dense metros this can swing value several percent on its own." },
  { factor: "Energy upgrades", effect: "Solar owned outright, new windows, or a heat pump can add value; leased solar often complicates a sale." },
  { factor: "Street and setting", effect: "A busy road, power lines, or backing onto commercial property is a real deduction a model rarely catches." },
  { factor: "Permits", effect: "Unpermitted square footage usually cannot be counted at all in an appraisal." },
];

export const APPRAISAL_TRIGGERS = [
  { id: "refi", label: "Refinancing", text: "Your lender orders the appraisal and uses that number, not an online estimate, to set your loan-to-value and your rate." },
  { id: "pmi", label: "Removing PMI", text: "Most servicers require a current appraisal or broker price opinion to confirm you have reached the equity threshold." },
  { id: "estate", label: "Estate settlement", text: "A date-of-death valuation from a licensed appraiser establishes the stepped-up basis. Screenshots of an AVM will not survive review." },
  { id: "divorce", label: "Divorce or buyout", text: "When one party buys the other out, both sides generally need a neutral, defensible number." },
  { id: "tax", label: "Property tax appeal", text: "Assessors respond to appraisals and closed comps, not to automated estimates." },
];

export const SOLD_HOMES_POINTS = [
  {
    label: "List price is an ask, sold price is the answer",
    text: "A neighbor listing at $625,000 tells you what they hoped for. A closed sale at $587,000 tells you what a real buyer with real financing paid. Only the second one is a comp.",
  },
  {
    label: "Watch the concessions",
    text: "A $600,000 sale with a $15,000 seller credit is effectively a $585,000 sale. Credits are disclosed in the transaction record, and a good agent nets them out before comparing.",
  },
  {
    label: "Price per square foot is a sanity check, not a valuation",
    text: "Cost per square foot falls as homes get bigger, so applying a small home's $/sqft to a large one overshoots badly. Use it to spot outliers, then adjust room by room.",
  },
  {
    label: "Days on market tells you the direction",
    text: "Comps that sold in a weekend above ask and comps that sat 90 days and cut twice imply very different pricing strategies today.",
  },
];

export const MARKET_FACTORS_2026 = [
  { id: "rates", label: "Mortgage rates", text: "Rates set the payment a buyer can carry. When rates move a point, the price a given buyer can afford moves roughly 10%, and asking prices follow with a lag of a few months." },
  { id: "inventory", label: "Inventory", text: "More homes for sale in your ZIP means more substitutes for yours, which compresses price. Track active listings in your price band, not the national headline." },
  { id: "insurance", label: "Insurance and taxes", text: "In wildfire, wind, and flood-exposed markets, premium increases are being priced into offers. A high-premium home now sells for measurably less than an identical one with a cheaper policy." },
  { id: "dom", label: "Days on market", text: "Rising median days on market is the earliest reliable signal that list prices in your area are ahead of what buyers will pay." },
  { id: "local", label: "Hyperlocal beats national", text: "National indexes and your ZIP frequently move in opposite directions in the same quarter. Comps within a mile of you outrank any national number." },
];

export const ESTIMATOR_DISCLAIMER =
  "This range is an automated estimate, not an appraisal. It is generated from ZIP-level market data and the characteristics you entered, data freshness varies by county, and it is not a valuation, an offer, or a commitment to lend. Refinancing, PMI removal, estate, tax, and legal use cases require review by a licensed appraiser.";

export const DISCLAIMER =
  "This page is educational information, not real estate, lending, tax, or legal advice. Estimate ranges shown here are automated and are not appraisals. Accuracy depends on the freshness and completeness of public records in your county, and unique or recently renovated homes will vary more. For refinancing, PMI removal, estate settlement, divorce, or a tax appeal, use a licensed appraiser. Confirm any number you plan to act on with a local licensed agent or appraiser.";

export const FAQS = [
  {
    id: "accurate",
    q: "How accurate is an online home value estimate?",
    a: "Automated estimates are usually within about 5–10% of the eventual sale price for typical homes in active markets with plenty of recent sales, and considerably less accurate for unique properties, rural areas, recently renovated homes, and homes that have not sold in decades. Treat the number as the middle of a range rather than a price, and always read the confidence note that comes with it.",
  },
  {
    id: "cma-vs-appraisal",
    q: "What is the difference between a CMA and an appraisal?",
    a: "A CMA is a pricing opinion prepared by a licensed real estate agent using recent comparable sales, usually free, and aimed at helping you set a list price. An appraisal is an independent valuation performed by a state-licensed appraiser following standardized methodology, typically costing $400–$800, and it is the number lenders, courts, and tax authorities accept. Use a CMA to price a sale; use an appraisal when a third party has to rely on the value.",
  },
  {
    id: "sold-vs-list",
    q: "Why do sold prices matter more than list prices?",
    a: "A list price is what a seller hoped to get; a sold price is what a financed buyer actually paid and what an appraiser was willing to support. Comparable analysis uses closed sales, adjusted for seller concessions, because that is the only evidence of what the market will bear. Active listings are useful only as a read on your current competition.",
  },
  {
    id: "ppsf",
    q: "Can I just use price per square foot?",
    a: "Only as a sanity check. Price per square foot falls as homes get larger, because land, kitchens, and mechanical systems do not scale linearly, so applying a small home's rate to a big one inflates the result. It also ignores condition, lot, layout, and location — the factors that drive most of the spread between two homes of identical size.",
  },
  {
    id: "improve",
    q: "Can I improve the estimate my home shows online?",
    a: "Partly. Most major portals let a homeowner claim their property and correct the bed, bath, square footage, and renovation details, which feeds the model better inputs. That fixes bad data; it does not override the comps. If the estimate is still far off after correcting the facts, the gap is usually condition or location, and only a CMA or appraisal will capture it.",
  },
  {
    id: "free",
    q: "Is a CMA really free?",
    a: "From most local agents, yes. Agents prepare CMAs to earn your listing, so there is no fee and no obligation to sell. Ask for the actual comps they used, the adjustments they applied, and their reasoning — a CMA without visible comps is just a number.",
  },
];

export const CTA = {
  label: "Get a local agent valuation",
  to: "/experts",
};

const SITE = "https://www.homeapproach.com";

/** WebPage + BreadcrumbList + FAQPage, baked into server HTML. */
export const HOME_VALUE_HUB_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};